import type { HealthieFormModule } from "@/lib/api/hooks";

type Answers = Readonly<Record<string, string>>;

/** Checkbox answers use Healthie's newline-separated option strings. */
function answerMatches(
  answer: string | undefined,
  values: readonly string[],
  moduleType: string,
): boolean {
  if (!answer) return false;
  const selected = moduleType === "checkbox" ? answer.split("\n") : [answer];
  return selected.some((value) => values.includes(value));
}

/** Apply explicit API rules only. Hidden parent answers cannot reveal children. */
export function visibleHealthieModules(
  modules: readonly HealthieFormModule[],
  answers: Answers,
): HealthieFormModule[] {
  const byId = new Map(modules.map((module) => [module.id, module]));
  const visibility = new Map<string, boolean>();
  const visiting = new Set<string>();
  const isVisible = (module: HealthieFormModule): boolean => {
    const cached = visibility.get(module.id);
    if (cached !== undefined) return cached;
    if (visiting.has(module.id)) return false;
    const rule = module.visible_when;
    if (!rule) return true;
    visiting.add(module.id);
    const parent = byId.get(rule.module_id);
    const visible = Boolean(
      parent &&
      isVisible(parent) &&
      answerMatches(answers[parent.id], rule.equals, parent.mod_type),
    );
    visiting.delete(module.id);
    visibility.set(module.id, visible);
    return visible;
  };
  return modules.filter(isVisible);
}

/** Submit only fields present in this form and currently shown to the patient. */
export function visibleHealthieAnswers(
  modules: readonly HealthieFormModule[],
  answers: Answers,
): Record<string, string> {
  return Object.fromEntries(
    visibleHealthieModules(modules, answers)
      .filter((module) =>
        Object.prototype.hasOwnProperty.call(answers, module.id),
      )
      .map((module) => [module.id, answers[module.id]]),
  );
}

export function disqualifiedHealthieModules(
  modules: readonly HealthieFormModule[],
  answers: Answers,
): HealthieFormModule[] {
  return visibleHealthieModules(modules, answers).filter((module) =>
    answerMatches(
      answers[module.id],
      module.disqualifying_values ?? [],
      module.mod_type,
    ),
  );
}

const NONE_OF_THE_ABOVE = /^none of (the above|these)$/i;

/**
 * Server-declared exclusive options win. A checkbox module the API sends
 * without any still treats a "None of the above" / "None of these" option as
 * exclusive, so a patient cannot submit it alongside a condition.
 */
export function healthieExclusiveOptions(module: HealthieFormModule): string[] {
  if (module.exclusive_options?.length) return module.exclusive_options;
  if (module.mod_type !== "checkbox") return [];
  return (module.options || "")
    .split("\n")
    .map((option) => option.trim())
    .filter((option) => NONE_OF_THE_ABOVE.test(option));
}

/** Only the given options are exclusive; other checkbox choices combine freely. */
export function toggleHealthieCheckbox(
  value: string,
  option: string,
  exclusiveOptions: readonly string[] = [],
): string {
  const selected = value.split("\n").filter(Boolean);
  if (selected.includes(option))
    return selected.filter((item) => item !== option).join("\n");
  if (exclusiveOptions.includes(option)) return option;
  return [
    ...selected.filter((item) => !exclusiveOptions.includes(item)),
    option,
  ].join("\n");
}

/** Restore saved choices using the current canonical option strings. */
export function hydrateHealthieChoiceAnswer(
  module: HealthieFormModule,
  value: string,
): string | undefined {
  const options = module.options
    .split("\n")
    .map((option) => option.trim())
    .filter(Boolean);
  const rawValues =
    module.mod_type === "checkbox" ? value.split("\n") : [value];
  const selected = [
    ...new Set(
      rawValues
        .map((raw) =>
          options.find(
            (option) => option.toLowerCase() === raw.trim().toLowerCase(),
          ),
        )
        .filter((option): option is string => Boolean(option)),
    ),
  ];
  if (!selected.length) return undefined;
  const exclusive = healthieExclusiveOptions(module);
  if (module.mod_type !== "checkbox" || !exclusive.length)
    return selected.join("\n");
  // Keep the substantive answers if an older draft also selected None.
  const ordinary = selected.filter((option) => !exclusive.includes(option));
  return (ordinary.length ? ordinary : selected.slice(0, 1)).join("\n");
}

/** Accept field IDs only from the API's structured validation details. */
export function healthieServerFieldErrors(
  error: unknown,
  modules: readonly HealthieFormModule[],
): { moduleId: string; message: string }[] {
  if (!error || typeof error !== "object" || !("details" in error)) return [];
  const details = error.details;
  if (
    !details ||
    typeof details !== "object" ||
    !("module_ids" in details) ||
    !Array.isArray(details.module_ids)
  )
    return [];
  const ids = new Set(
    details.module_ids.filter((id): id is string => typeof id === "string"),
  );
  const message =
    "message" in error && typeof error.message === "string"
      ? error.message
      : "Please check this answer.";
  return modules
    .filter((module) => ids.has(module.id))
    .map((module) => ({ moduleId: module.id, message }));
}
